var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { KompisElement, property } from '@inter-ikea-kompis/web-component';
import { html } from '@inter-ikea-kompis/web-component';
import { FontStyle } from '@inter-ikea-kompis/themes';
import { KeyboardInjectorFinder } from '@inter-ikea-kompis/component-keyboard';
import { Button as SkapaButton } from '@ingka/button-webc/passive.js';
import { Text } from '@ingka/common-styles-webc';
import { informationCircle, registerIcon } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import { InlineMessage as SkapaInlineMessage } from '@ingka/inline-message-webc/passive.js';
import { ModalFooter as SkapaModalFooter, ModalHeader as SkapaModalHeader, Prompt as SkapaPrompt } from '@ingka/modal-webc/passive.js';
import SaveDesignToProfileModalEnum from '../../enums/SaveDesignToProfileModalEnum.js';
import SaveDesignToProfileStyle from './SaveDesignToProfileStyle.js';
/**
 * This component is used to allow users to save their design to profile.
 *
 * @event modalClose Triggered when user clicks outside, or presses escape key.
 * @event modalOpen Triggered when the save button is clicked, and 'this.loggedIn' is false.
 * @event logInAndSaveDesign Triggered when the login button of the prompt is clicked. After successful login and user has been redirected to the planner, the planner should save the design immediately without prompting again.
 * @event saveDesign Triggered when the save button is clicked, and 'this.loggedIn' is true.
 *
 * @slot save-button - Optionally a custom button to trigger save to profile.
 */
export default class SaveDesignToProfile extends KompisElement {
    constructor() {
        super(...arguments);
        // Visible modal.
        this.visibleModal = null;
        // Whether the user is logged in to IKEA or not.
        this.loggedIn = false;
        this.promptElement = null;
    }
    render() {
        // In order to skip a querySelector during render we return the slot immediately. If there isn't anything slotted, this will render null.
        return html `
			<slot
				name="save-button"
				@keydown="${this.handleKeyDown.bind(this)}"
				tabindex="0"
				role="button"
				@click="${this.handleSaveButtonClick.bind(this)}"
				aria-hidden="${this.visibleModal === SaveDesignToProfileModalEnum.saveDesignToProfileCard}"
			></slot>
		`;
    }
    async connectedCallback() {
        registerIcon(informationCircle);
        this.customElements.define('skapa-prompt', SkapaPrompt);
        this.customElements.define('skapa-modal-header', SkapaModalHeader);
        this.customElements.define('skapa-modal-footer', SkapaModalFooter);
        this.customElements.define('skapa-button', SkapaButton);
        this.customElements.define('skapa-inline-message', SkapaInlineMessage);
        this.customElements.define('skapa-icon', SkapaIcon);
        super.connectedCallback();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.closePrompt();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('visibleModal')) {
            if (this.visibleModal === SaveDesignToProfileModalEnum.saveDesignToProfileCard) {
                this.openPrompt();
            }
            else {
                this.closePrompt();
            }
        }
        return true;
    }
    openPrompt() {
        if (this.promptElement) {
            return;
        }
        this.promptElement = this.createPrompt();
        const modalHeader = this.createModalHeader();
        const heading = this.createPromptHeading();
        const content = this.createPromptContent();
        const footer = this.createModalFooter();
        this.promptElement.appendChild(modalHeader);
        this.promptElement.appendChild(heading);
        this.promptElement.appendChild(content);
        this.promptElement.appendChild(footer);
        const parent = KeyboardInjectorFinder.findKeyboardInjectorElement() || document.body;
        parent.appendChild(this.promptElement);
        content.setAttribute('tabindex', '0');
        this.promptElement.open = true;
    }
    createPrompt() {
        const promptElement = this.createInternalElement('skapa-prompt');
        promptElement.setAttribute('aria-label', this.translations.saveDesignToProfileSaveDesignHeader);
        promptElement.style.fontFamily = FontStyle.getFontFamily(this.theme);
        promptElement.style.zIndex = '500';
        promptElement.addEventListener('closerequest', this.handleCloseRequest.bind(this));
        promptElement.addEventListener('closed', () => {
            promptElement?.parentElement?.removeChild(promptElement);
        });
        return promptElement;
    }
    createModalHeader() {
        const modalHeader = this.createInternalElement('skapa-modal-header');
        modalHeader.slot = 'header';
        const closeButton = document.createElement('span');
        closeButton.slot = 'closebutton-label';
        closeButton.innerText = this.translations.modalAriaLabelButtonClose;
        modalHeader.appendChild(closeButton);
        return modalHeader;
    }
    createPromptHeading() {
        const heading = document.createElement('h2');
        heading.slot = 'title';
        heading.innerText = this.translations.saveDesignToProfileSaveDesignHeader;
        heading.className = Text.classnames({ heading: { m: true } });
        const style = document.createElement('style');
        style.textContent = SaveDesignToProfileStyle.getStyle();
        heading.appendChild(style);
        return heading;
    }
    createPromptContent() {
        const body = this.createInternalElement('skapa-inline-message');
        body.subtle = true;
        const bodySpan = document.createElement('span');
        bodySpan.slot = 'body';
        bodySpan.innerText = this.translations.saveDesignToProfileLogInMessage;
        bodySpan.className = Text.classnames({ body: { l: true } });
        const style = document.createElement('style');
        style.textContent = Text.CSS;
        bodySpan.appendChild(style);
        body.appendChild(bodySpan);
        return body;
    }
    createModalFooter() {
        const footer = this.createInternalElement('skapa-modal-footer');
        footer.slot = 'footer';
        footer.addEventListener('primary', this.handlePrimaryClick.bind(this));
        const footerButton = this.createInternalElement('skapa-button');
        footerButton.slot = 'primary-button';
        footerButton.variant = 'emphasised';
        footerButton.innerText = this.translations.saveDesignToProfileLogInOrRegisterButtonLabel;
        footer.appendChild(footerButton);
        return footer;
    }
    closePrompt() {
        if (this.promptElement) {
            this.promptElement.open = false;
            this.promptElement = null;
        }
    }
    handleSaveButtonInteraction() {
        if (this.loggedIn) {
            this.dispatchEvent(new CustomEvent('saveDesign', { bubbles: true }));
        }
        else {
            this.dispatchEvent(new CustomEvent('modalOpen', {
                bubbles: true,
                detail: { visibleModal: SaveDesignToProfileModalEnum.saveDesignToProfileCard }
            }));
        }
    }
    handleKeyDown(e) {
        if (e.key === 'Enter' || e.key === 'Space') {
            e.preventDefault();
            this.handleSaveButtonInteraction();
        }
    }
    handleSaveButtonClick(e) {
        e.preventDefault();
        this.handleSaveButtonInteraction();
    }
    handleCloseRequest(e) {
        e.preventDefault();
        this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail: { visibleModal: null } }));
    }
    handlePrimaryClick(e) {
        e.preventDefault();
        this.dispatchEvent(new CustomEvent('logInAndSaveDesign', { bubbles: true }));
    }
}
__decorate([
    property({ type: Object, required: true })
], SaveDesignToProfile.prototype, "theme", void 0);
__decorate([
    property({ type: Object })
], SaveDesignToProfile.prototype, "translations", void 0);
__decorate([
    property({ type: String })
], SaveDesignToProfile.prototype, "visibleModal", void 0);
__decorate([
    property({ type: Boolean })
], SaveDesignToProfile.prototype, "loggedIn", void 0);
//# sourceMappingURL=SaveDesignToProfile.js.map